import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

interface NOVAREPoteauDetailProps {
  t: any;
  isRTL: boolean;
}

const ProductDetailPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 120px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  padding-bottom: 4rem;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    text-decoration: ${props => props.$isLast ? 'none' : 'underline'};
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #94a3b8;
  margin: 0 0.5rem;
`;

const ProductLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

const MainImage = styled.div`
  width: 100%;
  max-width: 500px;
  height: 400px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const ThumbnailImage = styled.div`
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    border-color: #dc2626;
    transform: translateY(-2px);
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    align-items: center;
  }
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ProductTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const TechnicalDatasheet = styled(motion.div)<{ $isRTL: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 300px;
  
  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const PDFIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #dc2626;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.8rem;
`;

const PDFText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PDFLabel = styled.span`
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
`;

const PDFSubtext = styled.span`
  font-size: 0.8rem;
  color: #64748b;
`;

const ProviderLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 300px;
  
  img {
    max-width: 120px;
    height: auto;
    object-fit: contain;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    max-width: 250px;
    align-self: center;
    margin: 1rem auto;
    
    img {
      max-width: 100px;
    }
  }
`;

const TabsSection = styled.div`
  margin-top: 3rem;
`;

const TabsHeader = styled.div`
  display: flex;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 2rem;
  gap: 0.5rem;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 1rem 1.5rem;
  border: none;
  background: none;
  font-weight: 600;
  cursor: pointer;
  color: ${props => props.$active ? '#dc2626' : '#64748b'};
  border-bottom: 2px solid ${props => props.$active ? '#dc2626' : 'transparent'};
  transition: all 0.3s ease;
  font-size: 1rem;

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    color: #dc2626;
  }
`;

const TabContent = styled(motion.div)`
  min-height: 400px;
`;

const DescriptionSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  text-align: left;
  direction: ltr;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    text-align: left;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #dc2626;
  padding-bottom: 0.5rem;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    text-align: left;
  }
`;

const DescriptionText = styled.p`
  color: #475569;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  text-align: left;
  
  @media (max-width: 768px) {
    text-align: left;
    font-size: 0.95rem;
  }
`;

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SpecCard = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
  border-left: 4px solid #dc2626;
  text-align: left;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 1.25rem;
    text-align: left;
  }
  
  &:hover {
    background: #e2e8f0;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const SpecCardTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-align: left;

  &::before {
    content: "⚡";
    font-size: 1.2rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: left;
  }
`;

const SpecCardContent = styled.div`
  color: #475569;
  line-height: 1.6;
  font-size: 0.95rem;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    text-align: left;
  }
`;

const ProductsSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const ProductCount = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  color: #64748b;
`;

const ProductsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ProductItem = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
  border-left: 4px solid #dc2626;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    background: #e2e8f0;
    transform: translateX(4px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const ProductItemInfo = styled.div`
  flex: 1;
`;

const ProductName = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
`;

const ProductReference = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const ProductPackaging = styled.div`
  color: #64748b;
  font-size: 0.9rem;
`;

const DownloadButton = styled.button`
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  white-space: nowrap;

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    background: #b91c1c;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(220, 38, 38, 0.3);
  }
`;

const ResourcesSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const ResourceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    background: #e2e8f0;
    transform: translateY(-1px);
  }
`;

const ResourceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ResourceName = styled.span`
  font-weight: 600;
  color: #1e293b;
`;

const ResourceSize = styled.span`
  font-size: 0.9rem;
  color: #64748b;
`;

const DownloadLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #dc2626;
  text-decoration: none;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    background: #dc2626;
    color: white;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const PaginationButton = styled.button<{ $active?: boolean; $disabled?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  background: ${props => props.$active ? '#dc2626' : 'white'};
  color: ${props => props.$active ? 'white' : '#64748b'};
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  white-space: nowrap;

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    background: ${props => props.$active ? '#dc2626' : '#f8fafc'};
    color: ${props => props.$active ? 'white' : '#374151'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const CharacteristicsSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const CharacteristicsSubsection = styled.div`
  margin-bottom: 2rem;
`;

const CharacteristicsSubtitle = styled.h4<{ $isExpanded: boolean }>`
  font-size: 1.2rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    color: #dc2626;
  }

  &::after {
    content: "▼";
    font-size: 0.8rem;
    transition: transform 0.3s ease;
    transform: ${props => props.$isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
  }
`;

const CharacteristicsList = styled.div<{ $isExpanded: boolean }>`
  border-top: 1px solid #e2e8f0;
  padding-top: 120px; // Account for fixed header
  max-height: ${props => props.$isExpanded ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
  padding-top: ${props => props.$isExpanded ? '1rem' : '0'};
`;

const CharacteristicItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const CharacteristicLabel = styled.span`
  font-weight: 500;
  color: #374151;
`;

const CharacteristicValue = styled.span`
  color: #6b7280;
  font-weight: 400;
`;

const NOVAREPoteauDetail: React.FC<NOVAREPoteauDetailProps> = ({ t, isRTL }) => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [activeTab, setActiveTab] = useState('description');
  const [selectedImage, setSelectedImage] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isElectricalExpanded, setIsElectricalExpanded] = useState(true);
  const itemsPerPage = 5;

  const breadcrumbs = [
    { label: "Catalogue produits", path: "/" },
    { label: "Transformateurs", path: "/category/transformateurs" },
    { label: "Transformateurs de distribution à huile", path: "/category/transformateurs" },
    { label: "NOVARE Poteau", path: "" }
  ];

  const images = [
    '/images/catalog/transformateurs/NOVARE Poteau.jpg',
    '/images/catalog/transformateurs/NOVARE Poteau.jpg'
  ];

  const products = [
    {
      id: 'h59-50kva-20kv',
      name: 'H59 50KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52112273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 50KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-50kva-22kv',
      name: 'H59 50KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52113273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 50KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-100kva-20kv',
      name: 'H59 100KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52122273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 100KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-100kva-22kv',
      name: 'H59 100KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52123273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 100KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-160kva-20kv',
      name: 'H59 160KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52132273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 160KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-160kva-22kv',
      name: 'H59 160KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52133273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 160KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-200kva-20kv',
      name: 'H59 200KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52142273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 200KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-200kva-22kv',
      name: 'H59 200KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52143273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 200KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-250kva-20kv',
      name: 'H59 250KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52152273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 250KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-250kva-22kv',
      name: 'H59 250KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52153273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 250KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-315kva-20kv',
      name: 'H59 315KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52162273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 315KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-315kva-22kv',
      name: 'H59 315KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52163273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 315KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-400kva-20kv',
      name: 'H59 400KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52172273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 400KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-400kva-22kv',
      name: 'H59 400KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52173273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 400KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-500kva-20kv',
      name: 'H59 500KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52182273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 500KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-500kva-22kv',
      name: 'H59 500KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52183273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 500KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-630kva-20kv',
      name: 'H59 630KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52192273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 630KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-630kva-22kv',
      name: 'H59 630KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52193273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 630KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-800kva-20kv',
      name: 'H59 800KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52202273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 800KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-800kva-22kv',
      name: 'H59 800KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52203273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 800KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-1000kva-20kv',
      name: 'H59 1000KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52212273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 1000KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-1000kva-22kv',
      name: 'H59 1000KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52113273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 1000KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-1250kva-20kv',
      name: 'H59 1250KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52222273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 1250KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-1250kva-22kv',
      name: 'H59 1250KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52223273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 1250KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-1600kva-20kv',
      name: 'H59 1600KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52232273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 1600KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-1600kva-22kv',
      name: 'H59 1600KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52233273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 1600KVA 22KV 400V ±5%.pdf'
    },
    {
      id: 'h59-2000kva-20kv',
      name: 'H59 2000KVA 20KV 400V ±5%',
      reference: 'Réf. Nexans 52242273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 2000KVA 20KV 400V ±5%.pdf'
    },
    {
      id: 'h59-2000kva-22kv',
      name: 'H59 2000KVA 22KV 400V ±5%',
      reference: 'Réf. Nexans 52243273111',
      packaging: 'Conditionnement: Unité',
      datasheet: '/images/catalog/transformateurs/NOVARE Poteau/produits/H59 2000KVA 22KV 400V ±5%.pdf'
    }
  ];

  const totalPages = Math.ceil(products.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = products.slice(startIndex, endIndex);

  const handleBreadcrumbClick = (path: string) => {
    if (path) {
      navigate(path);
    }
  };

  const handleDatasheetClick = () => {
    window.open('/images/catalog/transformateurs/NOVARE Poteau/NOVARE Poteau.pdf', '_blank');
  };

  const handleDownload = (datasheet: string) => {
    window.open(datasheet, '_blank');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleElectricalToggle = () => {
    setIsElectricalExpanded(!isElectricalExpanded);
  };

  return (
    <ProductDetailPage $isRTL={isRTL}>
      <Container>
        <Breadcrumbs
          $isRTL={isRTL}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              <BreadcrumbLink
                $isLast={index === breadcrumbs.length - 1}
                onClick={() => handleBreadcrumbClick(breadcrumb.path)}
              >
                {breadcrumb.label}
              </BreadcrumbLink>
              {index < breadcrumbs.length - 1 && <BreadcrumbSeparator>›</BreadcrumbSeparator>}
            </React.Fragment>
          ))}
        </Breadcrumbs>

        <ProductLayout>
          <ImageSection>
            <MainImage>
              <img src={images[selectedImage]} alt="NOVARE Poteau" />
            </MainImage>
            <ThumbnailImage onClick={() => setSelectedImage(0)}>
              <img src={images[0]} alt="NOVARE Poteau thumbnail" />
            </ThumbnailImage>
          </ImageSection>

          <ProductInfo>
            <ProductTitle>NOVARE Poteau</ProductTitle>
            <TechnicalDatasheet
              $isRTL={isRTL}
              onClick={handleDatasheetClick}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <PDFIcon>PDF</PDFIcon>
              <PDFText>
                <PDFLabel>Fiche technique</PDFLabel>
                <PDFSubtext>PDF</PDFSubtext>
              </PDFText>
            </TechnicalDatasheet>

            <ProviderLogo>
              <img 
                src="/images/partners/nexans.png" 
                alt="Nexans" 
              />
            </ProviderLogo>
          </ProductInfo>
        </ProductLayout>

        <TabsSection ref={ref}>
          <TabsHeader>
            <TabButton 
              $active={activeTab === 'description'} 
              onClick={() => setActiveTab('description')}
            >
              Description
            </TabButton>
            <TabButton 
              $active={activeTab === 'caracteristiques'} 
              onClick={() => setActiveTab('caracteristiques')}
            >
              Caractéristiques
            </TabButton>
            <TabButton 
              $active={activeTab === 'ressources'} 
              onClick={() => setActiveTab('ressources')}
            >
              Ressources
            </TabButton>
          </TabsHeader>

          <TabContent>
            {activeTab === 'description' && (
              <DescriptionSection>
                <SectionTitle>Description</SectionTitle>
                
                <SpecsGrid>
                  <SpecCard>
                    <SpecCardTitle>Normes</SpecCardTitle>
                    <SpecCardContent>
                      <strong>PRODUCT:</strong> IEC 60076<br/>
                      <strong>CEI 60076:</strong> Publications 1-2-3-4 et 5<br/>
                      <strong>D60-P60 ONEE:</strong> Branche Electricité
                    </SpecCardContent>
                  </SpecCard>
                  
                  <SpecCard>
                    <SpecCardTitle>Constitution</SpecCardTitle>
                    <SpecCardContent>
                      <strong>Équipements de base:</strong><br/>
                      • 3 traversées porcelaine HTA<br/>
                      • 4 traversées porcelaine BT<br/>
                      • Commutateur hors tension<br/>
                      • Soupape de suppression<br/>
                      • Orifice de remplissage<br/>
                      • Crochet normalisé sur face arrière<br/>
                      • Anneaux de levage<br/>
                      • Support équerre soudée sur fond de cuve<br/>
                      • 2 prises de mise à la terre<br/>
                      • Plaque signalétique, plomb de garantie
                    </SpecCardContent>
                  </SpecCard>
                  
                  <SpecCard>
                    <SpecCardTitle>Couleur de la cuve</SpecCardTitle>
                    <SpecCardContent>
                      <strong>RAL 7033:</strong> Gris Ciment
                    </SpecCardContent>
                  </SpecCard>
                  
                  <SpecCard>
                    <SpecCardTitle>Caractéristiques générales</SpecCardTitle>
                    <SpecCardContent>
                      <strong>Gamme de puissances:</strong> 50 - 100 - 160 KVA<br/>
                      <strong>Description:</strong> Transformateur hermétique immergé dans l'huile<br/>
                      <strong>Remplissage:</strong> Intégral HGL<br/>
                      <strong>Refroidissement:</strong> Naturel type ONAN<br/>
                      <strong>Installation:</strong> Extérieure
                    </SpecCardContent>
                  </SpecCard>
                  
                  <SpecCard>
                    <SpecCardTitle>Tensions de service</SpecCardTitle>
                    <SpecCardContent>
                      <strong>HTA:</strong> 11 - 15 - 20 - 22 - 30 et 33 KV<br/>
                      <strong>Niveau d'isolement:</strong> 24 - 36 KV<br/>
                      <strong>BT:</strong> 400 - 410 V<br/>
                      <em>Autres tensions spécifiques peuvent être réalisées à la demande</em>
                    </SpecCardContent>
                  </SpecCard>
                  
                  <SpecCard>
                    <SpecCardTitle>Réglage hors tension</SpecCardTitle>
                    <SpecCardContent>
                      <strong>Commutateurs à 3 positions:</strong> Sur enroulements HTA ±5%<br/>
                      <em>Autres réglages peuvent être fournis à la demande</em>
                    </SpecCardContent>
                  </SpecCard>
                </SpecsGrid>
              </DescriptionSection>
            )}

            {activeTab === 'caracteristiques' && (
              <CharacteristicsSection>
                <SectionTitle>Caractéristiques</SectionTitle>
                
                <CharacteristicsSubsection>
                  <CharacteristicsSubtitle 
                    $isExpanded={isElectricalExpanded}
                    onClick={handleElectricalToggle}
                  >
                    Caractéristiques électriques
                  </CharacteristicsSubtitle>
                  <CharacteristicsList $isExpanded={isElectricalExpanded}>
                    <CharacteristicItem>
                      <CharacteristicLabel>Tension de service maximale Uc</CharacteristicLabel>
                      <CharacteristicValue>24 - 36 kV</CharacteristicValue>
                    </CharacteristicItem>
                    <CharacteristicItem>
                      <CharacteristicLabel>Tension assignée d'isolement</CharacteristicLabel>
                      <CharacteristicValue>24 - 36 kV</CharacteristicValue>
                    </CharacteristicItem>
                    <CharacteristicItem>
                      <CharacteristicLabel>Fréquence assignée</CharacteristicLabel>
                      <CharacteristicValue>50 Hz</CharacteristicValue>
                    </CharacteristicItem>
                    <CharacteristicItem>
                      <CharacteristicLabel>Puissance assignée</CharacteristicLabel>
                      <CharacteristicValue>50 - 100 - 160 kVA</CharacteristicValue>
                    </CharacteristicItem>
                    <CharacteristicItem>
                      <CharacteristicLabel>Couplage</CharacteristicLabel>
                      <CharacteristicValue>Dyn11</CharacteristicValue>
                    </CharacteristicItem>
                    <CharacteristicItem>
                      <CharacteristicLabel>Réglage de tension</CharacteristicLabel>
                      <CharacteristicValue>±5% par paliers de 2.5%</CharacteristicValue>
                    </CharacteristicItem>
                    <CharacteristicItem>
                      <CharacteristicLabel>Tensions de service HTA</CharacteristicLabel>
                      <CharacteristicValue>11 - 15 - 20 - 22 - 30 et 33 kV</CharacteristicValue>
                    </CharacteristicItem>
                    <CharacteristicItem>
                      <CharacteristicLabel>Tensions de service BT</CharacteristicLabel>
                      <CharacteristicValue>400 - 410 V</CharacteristicValue>
                    </CharacteristicItem>
                  </CharacteristicsList>
                </CharacteristicsSubsection>
              </CharacteristicsSection>
            )}

            {activeTab === 'ressources' && (
              <ResourcesSection>
                <SectionTitle>Ressources</SectionTitle>
                
                <ResourceItem>
                  <ResourceInfo>
                    <ResourceName>Catalogue Transformateurs NOVARE</ResourceName>
                    <ResourceSize>PDF - 4.2 MB</ResourceSize>
                  </ResourceInfo>
                  <DownloadLink 
                    href="/images/catalog/transformateurs/NOVARE Poteau/ressources/Catalogue Transformateurs.pdf"
                    target="_blank"
                  >
                    <span>↓</span>
                    Télécharger
                  </DownloadLink>
                </ResourceItem>
              </ResourcesSection>
            )}
          </TabContent>
        </TabsSection>
      </Container>
    </ProductDetailPage>
  );
};

export default NOVAREPoteauDetail; 